import React, { useContext, useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { CartContext } from "./CartContext";
import productData from "../Datas/productData";
import siteConfig from "../config/siteConfig";
import "../css/productdetails.scss";

const money = new Intl.NumberFormat(siteConfig.locale || "en-US", {
  style: "currency",
  currency: siteConfig.currency || "USD",
});

const normalizeImage = (image) => {
  if (typeof image === "string") return image;
  return image?.src || "";
};

const getProductImages = (product) => {
  if (!product) return [];
  const images = [product.image, ...(Array.isArray(product.images) ? product.images : [])]
    .map(normalizeImage)
    .filter(Boolean);
  return [...new Set(images)];
};

const labelize = (value) =>
  String(value)
    .replace(/([a-z])([A-Z])/g, "$1 $2")
    .replace(/[-_]/g, " ")
    .replace(/\b\w/g, (character) => character.toUpperCase());

const getDescription = (description) =>
  Array.isArray(description) ? description : [description].filter(Boolean);

const ProductDetail = () => {
  const { id } = useParams();
  const cart = useContext(CartContext);
  const routeValue = (() => {
    try {
      return decodeURIComponent(id || "");
    } catch {
      return id || "";
    }
  })();
  const product = productData.find(
    (item) =>
      String(item.id) === String(routeValue) || item.slug === String(routeValue)
  );
  const [selectedImage, setSelectedImage] = useState(0);
  const [quantity, setQuantity] = useState(1);
  const [openAccordion, setOpenAccordion] = useState("details");
  const [addedMessage, setAddedMessage] = useState("");

  useEffect(() => {
    setSelectedImage(0);
    setQuantity(1);
    setOpenAccordion("details");
    setAddedMessage("");
  }, [product?.id]);

  if (!product) {
    return (
      <main className="product-detail product-detail--not-found">
        <section className="product-not-found" aria-labelledby="product-not-found-title">
          <span className="product-detail__eyebrow">404 · Product not found</span>
          <h1 id="product-not-found-title">That good thing got away.</h1>
          <p>
            The item may have sold out or moved. There is plenty more to taste
            at {siteConfig.brandName}.
          </p>
          <div className="product-not-found__actions">
            <Link className="product-detail__button" to="/shop">
              Browse all products
            </Link>
            <Link className="product-detail__text-link" to="/">
              Return home
            </Link>
          </div>
        </section>
      </main>
    );
  }

  const images = getProductImages(product);
  const stock = Math.max(0, Number(product.stock) || 0);
  const currentImage = images[selectedImage] || images[0] || "";
  const attributes = Object.entries(product.attributes || {}).filter(
    ([, value]) => value !== undefined && value !== null && value !== ""
  );
  const isWishlisted = cart.isWishlisted
    ? cart.isWishlisted(product.id)
    : (cart.wishlistItems || cart.wishlist || []).includes(product.id);
  const relatedByCategory = productData.filter(
    (item) => item.id !== product.id && item.category === product.category
  );
  const productTags = new Set(product.tags || []);
  const relatedByTags = productData
    .filter(
      (item) =>
        item.id !== product.id &&
        item.category !== product.category &&
        (item.tags || []).some((tag) => productTags.has(tag))
    )
    .sort(
      (left, right) =>
        (right.tags || []).filter((tag) => productTags.has(tag)).length -
        (left.tags || []).filter((tag) => productTags.has(tag)).length
    );
  const remainingProducts = productData.filter(
    (item) =>
      item.id !== product.id &&
      !relatedByCategory.some((related) => related.id === item.id) &&
      !relatedByTags.some((related) => related.id === item.id)
  );
  const recommendations = [
    ...relatedByCategory,
    ...relatedByTags,
    ...remainingProducts,
  ].slice(0, 4);

  const changeQuantity = (nextQuantity) => {
    setQuantity(Math.max(1, Math.min(stock || 1, nextQuantity)));
    setAddedMessage("");
  };

  const handleAddToCart = () => {
    if (!stock) return;
    cart.addToCart(product.id, quantity);
    setAddedMessage(`${quantity} × ${product.name} added to your cart.`);
  };

  const handleWishlist = () => {
    if (cart.toggleWishlist) {
      cart.toggleWishlist(product.id);
    } else if (isWishlisted && cart.removeFromWishlist) {
      cart.removeFromWishlist(product.id);
    } else if (cart.addToWishlist) {
      cart.addToWishlist(product.id);
    }
  };

  const toggleAccordion = (section) => {
    setOpenAccordion((current) => (current === section ? null : section));
  };

  const goToProductTop = () => {
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  return (
    <main className="product-detail">
      <div className="product-detail__shell">
        <nav className="product-breadcrumbs" aria-label="Breadcrumb">
          <ol>
            <li>
              <Link to="/">Home</Link>
            </li>
            <li>
              <Link to="/shop">Shop</Link>
            </li>
            <li>
              <Link to={`/shop?category=${encodeURIComponent(product.category)}`}>
                {product.category}
              </Link>
            </li>
            <li aria-current="page">{product.name}</li>
          </ol>
        </nav>

        <article
          className="product-detail__product"
          itemScope
          itemType="https://schema.org/Product"
        >
          <meta itemProp="sku" content={product.sku || String(product.id)} />
          <div className="product-gallery">
            <div className="product-gallery__main">
              {product.badge && (
                <span className="product-gallery__badge">{product.badge}</span>
              )}
              <img
                src={currentImage}
                alt={product.name}
                width="900"
                height="900"
                loading="lazy"
                decoding="async"
                itemProp="image"
              />
              {images.length > 1 && (
                <span className="product-gallery__count">
                  {selectedImage + 1} / {images.length}
                </span>
              )}
            </div>

            {images.length > 1 && (
              <div className="product-gallery__thumbs" aria-label="Product images">
                {images.map((image, index) => (
                  <button
                    className={
                      index === selectedImage
                        ? "product-gallery__thumb is-active"
                        : "product-gallery__thumb"
                    }
                    type="button"
                    key={image}
                    onClick={() => setSelectedImage(index)}
                    aria-label={`Show ${product.name} image ${index + 1}`}
                    aria-pressed={index === selectedImage}
                  >
                    <img
                      src={image}
                      alt=""
                      width="96"
                      height="96"
                      loading="lazy"
                      decoding="async"
                    />
                  </button>
                ))}
              </div>
            )}
          </div>

          <div className="product-purchase">
            <div className="product-purchase__heading">
              <div className="product-purchase__category-line">
                <Link to={`/shop?category=${encodeURIComponent(product.category)}`}>
                  {product.category}
                </Link>
                {product.newArrival && <span>New arrival</span>}
              </div>
              <h1 itemProp="name">{product.name}</h1>
              <div
                className="product-purchase__price"
                itemProp="offers"
                itemScope
                itemType="https://schema.org/Offer"
              >
                <meta itemProp="priceCurrency" content={siteConfig.currency || "USD"} />
                <meta itemProp="price" content={String(product.price)} />
                <meta
                  itemProp="availability"
                  content={
                    stock
                      ? "https://schema.org/InStock"
                      : "https://schema.org/OutOfStock"
                  }
                />
                <strong>{money.format(Number(product.price) || 0)}</strong>
              </div>
              {product.serves && (
                <p className="product-purchase__serves">
                  <span aria-hidden="true">●</span> Serves {product.serves}
                </p>
              )}
              {getDescription(product.description).map((paragraph, index) => (
                <p className="product-purchase__description" itemProp="description" key={index}>
                  {paragraph}
                </p>
              ))}
            </div>

            <fieldset className="purchase-options">
              <legend>Purchase options</legend>
                <label
                  className="purchase-option is-selected"
                >
                  <input
                    type="radio"
                    name="purchase-option"
                    value="one-time"
                    checked
                    readOnly
                  />
                  <span className="purchase-option__control" aria-hidden="true" />
                  <span>
                    <strong>One-time purchase</strong>
                    <small>Perfect for this table, gift, or gathering.</small>
                  </span>
                  <strong>{money.format(Number(product.price) || 0)}</strong>
                </label>
            </fieldset>

            <div className="product-stock" aria-live="polite">
              <span
                className={stock ? "product-stock__dot" : "product-stock__dot is-empty"}
                aria-hidden="true"
              />
              {stock ? (
                <span>
                  In stock · {stock} available
                  {stock <= 10 ? " — order soon" : ""}
                </span>
              ) : (
                <span>Currently out of stock</span>
              )}
            </div>

            <div className="product-purchase__actions">
              <div
                className="product-quantity"
                role="group"
                aria-label={`Quantity for ${product.name}`}
              >
                <button
                  type="button"
                  onClick={() => changeQuantity(quantity - 1)}
                  disabled={quantity === 1 || !stock}
                  aria-label="Decrease quantity"
                >
                  <span aria-hidden="true">−</span>
                </button>
                <output aria-live="polite" aria-label="Quantity">
                  {quantity}
                </output>
                <button
                  type="button"
                  onClick={() => changeQuantity(quantity + 1)}
                  disabled={!stock || quantity >= stock}
                  aria-label="Increase quantity"
                >
                  <span aria-hidden="true">+</span>
                </button>
              </div>
              <button
                className="product-purchase__add"
                type="button"
                onClick={handleAddToCart}
                disabled={!stock}
              >
                {stock ? "Add to cart" : "Sold out"}
              </button>
              <button
                className={
                  isWishlisted
                    ? "product-purchase__wishlist is-active"
                    : "product-purchase__wishlist"
                }
                type="button"
                onClick={handleWishlist}
                aria-label={
                  isWishlisted
                    ? `Remove ${product.name} from wishlist`
                    : `Add ${product.name} to wishlist`
                }
                aria-pressed={isWishlisted}
              >
                <svg viewBox="0 0 24 24" aria-hidden="true" focusable="false">
                  <path d="M20.8 4.7a5.5 5.5 0 0 0-7.8 0L12 5.8l-1.1-1.1a5.5 5.5 0 1 0-7.8 7.8l1.1 1.1L12 21l7.8-7.4 1.1-1.1a5.5 5.5 0 0 0-.1-7.8Z" />
                </svg>
              </button>
            </div>

            <div className="product-add-status" aria-live="polite" role="status">
              {addedMessage && (
                <>
                  <span aria-hidden="true">✓</span>
                  <p>
                    <strong>{addedMessage}</strong>
                  </p>
                  <Link to="/cartpage">View cart</Link>
                </>
              )}
            </div>

            <aside className="product-gift-note">
              <span aria-hidden="true">✦</span>
              <p>
                <strong>Sending a gift?</strong>
                Add your message after payment when customer care confirms the order.
              </p>
            </aside>

            <div className="product-accordions">
              <section className="product-accordion">
                <h2>
                  <button
                    id={`accordion-details-${product.id}`}
                    type="button"
                    onClick={() => toggleAccordion("details")}
                    aria-expanded={openAccordion === "details"}
                    aria-controls={`accordion-panel-details-${product.id}`}
                  >
                    The details &amp; ingredients
                    <span aria-hidden="true">
                      {openAccordion === "details" ? "−" : "+"}
                    </span>
                  </button>
                </h2>
                {openAccordion === "details" && (
                  <div
                    className="product-accordion__panel"
                    id={`accordion-panel-details-${product.id}`}
                    role="region"
                    aria-labelledby={`accordion-details-${product.id}`}
                  >
                    <dl className="product-attributes">
                      {product.weight && (
                        <div>
                          <dt>Weight</dt>
                          <dd>{product.weight}</dd>
                        </div>
                      )}
                      {product.serves && (
                        <div>
                          <dt>Serves</dt>
                          <dd>{product.serves}</dd>
                        </div>
                      )}
                      {attributes.map(([label, value]) => (
                        <div key={label}>
                          <dt>{labelize(label)}</dt>
                          <dd>{String(value)}</dd>
                        </div>
                      ))}
                      {product.sku && (
                        <div>
                          <dt>SKU</dt>
                          <dd>{product.sku}</dd>
                        </div>
                      )}
                    </dl>
                    {product.ingredients && (
                      <div className="product-ingredients">
                        <h3>Ingredients</h3>
                        <p>{product.ingredients}</p>
                        <small>
                          Please review the physical package for the most current
                          ingredient and allergen information.
                        </small>
                      </div>
                    )}
                  </div>
                )}
              </section>

              <section className="product-accordion">
                <h2>
                  <button
                    id={`accordion-shipping-${product.id}`}
                    type="button"
                    onClick={() => toggleAccordion("shipping")}
                    aria-expanded={openAccordion === "shipping"}
                    aria-controls={`accordion-panel-shipping-${product.id}`}
                  >
                    Shipping &amp; freshness
                    <span aria-hidden="true">
                      {openAccordion === "shipping" ? "−" : "+"}
                    </span>
                  </button>
                </h2>
                {openAccordion === "shipping" && (
                  <div
                    className="product-accordion__panel"
                    id={`accordion-panel-shipping-${product.id}`}
                    role="region"
                    aria-labelledby={`accordion-shipping-${product.id}`}
                  >
                    <p>
                      Perishable items are packed with insulated liners and cold
                      packs when needed. Standard shipping is a flat{" "}
                      {money.format(Number(siteConfig.shippingRate) || 9.99)} per order.
                    </p>
                    <p>
                      We time temperature-sensitive shipments to avoid weekend
                      delays and stand behind every fresh arrival.
                    </p>
                    <Link to="/shipping">Read our shipping policy</Link>
                  </div>
                )}
              </section>

              <section className="product-accordion">
                <h2>
                  <button
                    id={`accordion-story-${product.id}`}
                    type="button"
                    onClick={() => toggleAccordion("story")}
                    aria-expanded={openAccordion === "story"}
                    aria-controls={`accordion-panel-story-${product.id}`}
                  >
                    Our story
                    <span aria-hidden="true">
                      {openAccordion === "story" ? "−" : "+"}
                    </span>
                  </button>
                </h2>
                {openAccordion === "story" && (
                  <div
                    className="product-accordion__panel"
                    id={`accordion-panel-story-${product.id}`}
                    role="region"
                    aria-labelledby={`accordion-story-${product.id}`}
                  >
                    <p>{siteConfig.description}</p>
                    <p>
                      We choose every product for character, craft, and the joy
                      it brings to a shared table.
                    </p>
                    <Link to="/about">Meet {siteConfig.brandName}</Link>
                  </div>
                )}
              </section>
            </div>
          </div>
        </article>

        {recommendations.length > 0 && (
          <section className="product-recommendations" aria-labelledby="recommendations-title">
            <div className="product-recommendations__heading">
              <div>
                <span className="product-detail__eyebrow">More to savor</span>
                <h2 id="recommendations-title">You might also like</h2>
              </div>
              <Link to={`/shop?category=${encodeURIComponent(product.category)}`}>
                Shop {product.category}
              </Link>
            </div>
            <div className="product-recommendations__grid">
              {recommendations.map((recommendation) => (
                <article className="recommendation-card" key={recommendation.id}>
                  <Link
                    className="recommendation-card__image"
                    to={`/shopDetails/${recommendation.slug || recommendation.id}`}
                    onClick={goToProductTop}
                    aria-label={`View ${recommendation.name}`}
                  >
                    <img
                      src={getProductImages(recommendation)[0]}
                      alt={recommendation.name}
                      width="480"
                      height="480"
                      loading="lazy"
                      decoding="async"
                    />
                    {recommendation.badge && <span>{recommendation.badge}</span>}
                  </Link>
                  <p>{recommendation.category}</p>
                  <h3>
                    <Link
                      to={`/shopDetails/${recommendation.slug || recommendation.id}`}
                      onClick={goToProductTop}
                    >
                      {recommendation.name}
                    </Link>
                  </h3>
                  <strong>{money.format(Number(recommendation.price) || 0)}</strong>
                </article>
              ))}
            </div>
          </section>
        )}
      </div>
    </main>
  );
};

export default ProductDetail;
